import {Anchor, Button, LinkExternal01Icon} from '@translated/babele-react'
import clsx from 'clsx'
import {Trans, useTranslation} from 'react-i18next'
import {TAppLink, TLink} from '~/features/TLink'
import {HistoryCheckIcon} from './HistoryCheckIcon'
import {HistoryPointIcon} from './HistoryPointIcon'
import {SingularityImage} from './SingularityImage'
import {StatisticsImage} from './StatisticsImage'
import {TranslatedLogo} from './TranslatedLogo'
import {ProPlanIcon} from '~/routes/about/Illustrations/ProPlanIcon'
import {TeamPlanIcon} from '~/routes/about/Illustrations/TeamPlanIcon'
import {EnterprisePlanIcon} from '~/routes/about/Illustrations/EnterprisePlanIcon'
import {enrichedMeta} from '~/utils/meta.server'
import {MetaFunction, ActionFunctionArgs} from 'react-router'
import {useRootLoaderData} from '~/utils/useRootLoaderData'

export const handle = {
  i18n: 'about'
}

export const meta: MetaFunction<typeof loader> = ({data}) => {
  if (!data?.meta) return []

  return data.meta
}

export const loader = async ({request}: ActionFunctionArgs) => {
  return {
    meta: await enrichedMeta({
      with: ['og'],
      request,
      namespace: 'about'
    })
  }
}

export default function About() {
  const {t} = useTranslation('about')
  const {isAuthenticated} = useRootLoaderData()

  const translateNowBtn = isAuthenticated ? (
    <TAppLink to="/translate">{t('hero_section.translate_now_button')}</TAppLink>
  ) : (
    <TLink to="/translate" noRedirect>
      {t('hero_section.translate_now_button')}
    </TLink>
  )

  return (
    <main className="w-full text-neutral-1000" data-theme="translated-light">
      <section className="flex flex-col items-center gap-6 px-3 py-16 text-center text-neutral-25 lg:gap-10 lg:px-4 lg:py-[6rem]">
        <div className="flex flex-col gap-2">
          <h1 className="max-w-[60rem] text-[2.5rem] font-bold leading-none lg:text-[4.5rem]">
            {t('hero_section.title')}
          </h1>
        </div>
        <p className="max-w-[60rem] translated-light:text-light-violet-400 lg:text-lg">
          <Trans i18nKey="hero_section.description" t={t} components={{br: <br />}} />
        </p>
        <Button render={translateNowBtn} variant={{size: 'xl'}} className="hidden lg:flex">
          {t('hero_section.translate_now_button')}
        </Button>
        <Button render={translateNowBtn} variant={{size: 'lg'}} className="flex lg:hidden">
          {t('hero_section.translate_now_button')}
        </Button>
      </section>
      <section className="rounded-t-lg bg-neutral-25 px-4">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-8 py-16 lg:gap-12 lg:py-[6rem]">
          <div className="flex flex-col items-center gap-16 lg:gap-[6rem]">
            <h2 className="max-w-[60rem] text-center text-display-md font-bold lg:text-display-lg">
              {t('promotion_section.title')}
            </h2>
            <div className="flex max-w-[60rem] flex-col items-center gap-2 text-center">
              <h3 className="text-display-sm font-medium lg:text-display-md">
                {t('promotion_section.claims.0.subtitle')}
              </h3>
              <p className="text-lg text-neutral-800">
                {t('promotion_section.claims.0.description')}
              </p>
            </div>
            <div className="border-charts w-full lg:mx-[-6rem]">
              <div dir="ltr">
                <StatisticsImage />
              </div>
              <p className="mx-auto max-w-[75%] py-[2em] text-center text-[2vw] leading-[1.2] lg:text-[1.1vw]">
                {t('statistics_image.disclaimer')}
              </p>
            </div>
            <div className="flex max-w-[60rem] flex-col items-center gap-2 text-center">
              <h3 className="text-display-sm font-medium lg:text-display-md">
                {t('promotion_section.claims.1.subtitle')}
              </h3>
              <p className="text-lg text-neutral-800">
                <Trans
                  i18nKey="promotion_section.claims.1.description"
                  t={t}
                  components={{
                    1: (
                      <Anchor
                        className="font-regular"
                        target="_blank"
                        href="https://translated.com/speed-to-singularity"
                      />
                    )
                  }}
                />
              </p>
            </div>
            <div className="border-charts w-full lg:mx-[-6rem]">
              <div dir="ltr">
                <SingularityImage />
              </div>
              <p className="mx-auto max-w-[75%] py-[2em] text-center text-[2vw] leading-[1.2] lg:text-[1.1vw]">
                {t('statistics_image.disclaimer')}
              </p>
            </div>
          </div>
          <Button
            className="hidden lg:flex"
            variant={{mode: 'outline', size: 'lg'}}
            render={<TLink to="/lara-evaluation">{t('promotion_section.dive_deep_link')}</TLink>}
          >
            {t('promotion_section.dive_deep_link')}
            {/* TODO: We should use LinkExternal02Icon */}
            <LinkExternal01Icon />
          </Button>
          <Button
            className="flex lg:hidden"
            variant={{mode: 'outline', size: 'md'}}
            render={<TLink to="/lara-evaluation">{t('promotion_section.dive_deep_link')}</TLink>}
          >
            {t('promotion_section.dive_deep_link')}
            {/* TODO: We should use LinkExternal02Icon */}
            <LinkExternal01Icon />
          </Button>
        </div>
      </section>
      <section className="bg-light-violet-100 px-4">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-16 py-16 lg:gap-[6rem] lg:py-[6rem]">
          <h2 className="max-w-[60rem] text-center text-display-md font-bold lg:text-display-lg">
            {t('how_it_works_section.title')}
          </h2>
          <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-2 lg:gap-12">
            {/* TODO: Alt attribute, missing shadow border */}
            <div>
              <img
                className="w-full rounded-sm"
                src="/images/about/how_lara_works_1.webp"
                alt="tbd"
              />
            </div>
            <div className="flex flex-col gap-2">
              <h3 className="text-display-sm font-medium lg:text-display-md">
                {t('how_it_works_section.feature_1.subtitle')}
              </h3>
              <p className="text-neutral-800">
                <Trans
                  i18nKey="how_it_works_section.feature_1.description"
                  t={t}
                  components={{1: <span className="font-medium text-neutral-1000" />}}
                />
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-2 lg:gap-12">
            <div className="order-2 flex flex-col gap-2 lg:order-1">
              <h3 className="text-display-sm font-medium lg:text-display-md">
                {t('how_it_works_section.feature_2.subtitle')}
              </h3>
              <p className="text-neutral-800">
                <Trans
                  i18nKey="how_it_works_section.feature_2.description"
                  t={t}
                  components={{1: <span className="font-medium text-neutral-1000" />}}
                />
              </p>
            </div>
            {/* TODO: Alt attribute, missing shadow border */}
            <img
              className="order-1 w-full rounded-sm lg:order-2"
              src="/images/about/how_lara_works_2.webp"
              alt="tbd"
            />
          </div>
          <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-2 lg:gap-12">
            {/* TODO: Alt attribute, missing shadow border */}
            <img
              className="w-full rounded-sm"
              src="/images/about/how_lara_works_3.webp"
              alt="tbd"
            />
            <div className="flex flex-col gap-2">
              <h3 className="text-display-sm font-medium lg:text-display-md">
                {t('how_it_works_section.feature_3.subtitle')}
              </h3>
              <p className="text-neutral-800">
                <Trans
                  i18nKey="how_it_works_section.feature_3.description"
                  t={t}
                  components={{
                    1: <span className="font-medium text-neutral-1000" />,
                    3: <span className="font-medium text-neutral-1000" />,
                    5: <span className="font-medium text-neutral-1000" />,
                    7: <span className="font-medium text-neutral-1000" />
                  }}
                />
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 items-center gap-6 lg:grid-cols-2 lg:gap-12">
            <div className="order-2 flex flex-col gap-2 lg:order-1">
              <h3 className="text-display-sm font-medium lg:text-display-md">
                {t('how_it_works_section.feature_4.subtitle')}
              </h3>
              <p className="text-neutral-800">{t('how_it_works_section.feature_4.description')}</p>
            </div>
            {/* TODO: Alt attribute, missing shadow border */}
            <img
              className="order-1 w-full rounded-sm lg:order-2"
              src="/images/about/how_lara_works_4.webp"
              alt="tbd"
            />
          </div>
        </div>
      </section>
      <section className="bg-neutral-25 px-4">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-16 py-16 lg:gap-[6rem] lg:py-[6rem]">
          <h2 className="max-w-[60rem] text-center text-display-md font-bold lg:text-display-lg">
            {t('history_section.title')}
          </h2>
          <div className="grid grid-cols-1 gap-16 px-3 lg:grid-cols-[30rem_1fr] lg:gap-[6rem]">
            <div>
              <p className="text-center text-display-sm font-medium lg:text-left lg:text-display-md">
                <Trans
                  i18nKey="history_section.description"
                  t={t}
                  components={{1: <span className="text-purple-1000" />}}
                />
              </p>
            </div>
            <div>
              <div className="grid grid-cols-[5rem_1fr] gap-6">
                <div className="flex flex-col items-center justify-center gap-2 pt-4">
                  <HistoryCheckIcon />
                  <span className="text-display-sm">2011</span>
                </div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-display-sm font-medium lg:text-display-md">
                    {t('history_section.steps.year2011.title')}
                  </h3>
                  <p className="text-neutral-800 lg:text-lg">
                    <Trans
                      i18nKey="history_section.steps.year2011.description"
                      t={t}
                      components={{
                        1: <Anchor target="_blank" href="https://www.matecat.com/" />
                      }}
                    />
                  </p>
                </div>
              </div>
              <div className="ml-10 h-16 border-l-2 border-success-600 lg:h-[6rem]" />
              <div className="grid grid-cols-[5rem_1fr] gap-6">
                <div className="flex flex-col items-center justify-center gap-2 pt-4">
                  <HistoryCheckIcon />
                  <span className="text-display-sm">2017</span>
                </div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-display-sm font-medium lg:text-display-md">
                    {t('history_section.steps.year2017.title')}
                  </h3>
                  <p className="text-neutral-800 lg:text-lg">
                    <Trans
                      i18nKey="history_section.steps.year2017.description"
                      t={t}
                      components={{1: <Anchor target="_blank" href="https://www.modernmt.com/" />}}
                    />
                  </p>
                </div>
              </div>
              <div className="ml-10 h-16 border-l-2 border-success-600 lg:h-[6rem]" />
              <div className="grid grid-cols-[5rem_1fr] gap-6">
                <div className="flex flex-col items-center justify-center gap-2 pt-4">
                  <HistoryCheckIcon />
                  <span className="text-display-sm">2023</span>
                </div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-display-sm font-medium lg:text-display-md">
                    {t('history_section.steps.year2023.title')}
                  </h3>
                  <p className="text-neutral-800 lg:text-lg">
                    <Trans
                      i18nKey="history_section.steps.year2023.description"
                      t={t}
                      components={{
                        1: (
                          <Anchor
                            className="font-regular"
                            target="_blank"
                            href="https://translated.com/gartner-market-guide-ai-translation-services-2022"
                          />
                        ),
                        3: (
                          <Anchor
                            className="font-regular"
                            target="_blank"
                            href="https://translated.com/best-responsive-machine-translation"
                          />
                        ),
                        5: (
                          <Anchor
                            className="font-regular"
                            target="_blank"
                            href="https://translated.com/machine-translation-leader-IDC"
                          />
                        )
                      }}
                    />
                  </p>
                </div>
              </div>
              <div className="ml-10 h-16 border-l-2 border-success-600 lg:h-[6rem]" />
              <div className="grid grid-cols-[5rem_1fr] gap-6">
                <div className="flex flex-col items-center justify-center gap-2 pt-4">
                  <HistoryCheckIcon />
                  <span className="text-display-sm">2024</span>
                </div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-display-sm font-medium lg:text-display-md">
                    {t('history_section.steps.year2024.title')}
                  </h3>
                  <p className="text-neutral-800 lg:text-lg">
                    {t('history_section.steps.year2024.description')}
                  </p>
                </div>
              </div>
              <div className="ml-10 h-[6rem] border-l-2 border-dotted border-neutral-600" />
              <div className="grid grid-cols-[5rem_1fr] gap-6">
                <div className="flex flex-col items-center justify-center gap-2 pt-4">
                  <HistoryPointIcon />
                  <span className="text-display-sm text-neutral-800">2025</span>
                </div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-display-sm font-medium lg:text-display-md">
                    {t('history_section.steps.year2025.title')}
                  </h3>
                  <p className="text-neutral-800 lg:text-lg">
                    {t('history_section.steps.year2025.description')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-light-violet-100 px-4">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-16 py-16 lg:gap-[6rem] lg:py-[6rem]">
          <h2 className="max-w-[60rem] text-center text-display-md font-bold lg:text-display-lg">
            {t('start_now_section.title')}
          </h2>
          <div className="grid grid-cols-1 gap-[6rem] lg:grid-cols-2">
            <div className="flex flex-col gap-12">
              <div
                className={clsx(
                  'hidden w-full gap-4 rounded-md p-4 lg:flex',
                  "bg-[url('/images/about/translating_with_lara_1.webp')] bg-cover bg-center"
                )}
              >
                <div className="rounded-md bg-neutral-25/5 p-4 backdrop-blur-md">
                  <ProPlanIcon />
                </div>
                <div className="rounded-md bg-neutral-25/5 p-4 backdrop-blur-md">
                  <TeamPlanIcon />
                </div>
              </div>
              <div className="flex justify-center lg:hidden">
                <div
                  className={clsx(
                    'flex gap-4 rounded-md p-4',
                    "bg-[url('/images/about/translating_with_lara_1.webp')] bg-cover bg-center"
                  )}
                >
                  <div className="rounded-md bg-neutral-25/5 p-4 backdrop-blur-md">
                    <ProPlanIcon />
                  </div>
                  <div className="rounded-md bg-neutral-25/5 p-4 backdrop-blur-md">
                    <TeamPlanIcon />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2 text-center lg:text-left">
                <h3 className="text-display-sm lg:text-display-md">
                  {t('start_now_section.individuals.title')}
                </h3>
                <p className="text-neutral-800">{t('start_now_section.individuals.description')}</p>
              </div>
              <div className="flex justify-center gap-8 lg:justify-start">
                <Button
                  variant={{size: 'lg'}}
                  render={
                    <TAppLink to="/sign-up">
                      {t('start_now_section.individuals.sign_up_button')}
                    </TAppLink>
                  }
                >
                  {t('start_now_section.individuals.sign_up_button')}
                </Button>
                <Anchor
                  className="font-regular !text-neutral-1000"
                  render={
                    <TAppLink to="/pricing">
                      {t('start_now_section.individuals.view_plans_link')}
                    </TAppLink>
                  }
                >
                  {t('start_now_section.individuals.view_plans_link')}
                </Anchor>
              </div>
            </div>
            <div className="flex flex-col gap-12">
              <div
                className={clsx(
                  'hidden w-full gap-4 rounded-md p-4 lg:flex',
                  "bg-[url('/images/about/translating_with_lara_2.webp')] bg-cover bg-center"
                )}
              >
                <div className="rounded-md bg-neutral-25/5 p-4 backdrop-blur-md">
                  <EnterprisePlanIcon />
                </div>
              </div>
              <div className="flex justify-center lg:hidden">
                <div
                  className={clsx(
                    'flex gap-4 rounded-md p-4',
                    "bg-[url('/images/about/translating_with_lara_2.webp')] bg-cover bg-center"
                  )}
                >
                  <div className="rounded-md bg-neutral-25/5 p-4 backdrop-blur-md">
                    <EnterprisePlanIcon />
                  </div>
                </div>
              </div>
              <div className="flex flex-col gap-2 text-center lg:text-left">
                <h3 className="text-display-sm lg:text-display-md">
                  {t('start_now_section.companies.title')}
                </h3>
                <p className="text-neutral-800">{t('start_now_section.companies.description')}</p>
              </div>
              <div className="flex justify-center lg:justify-start">
                <Button
                  variant={{size: 'lg'}}
                  render={
                    <TLink to={'/sales'}>{t('start_now_section.companies.contact_button')}</TLink>
                  }
                />
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="bg-neutral-25 px-4">
        <div className="mx-auto flex max-w-7xl flex-col items-center gap-16 py-16 lg:gap-[6rem] lg:py-[6rem]">
          <h2 className="max-w-[60rem] text-center text-display-md font-bold lg:text-display-lg">
            {t('movie_section.title')}
          </h2>
          <p className="max-w-[60rem] text-center text-display-sm lg:text-display-md">
            {t('movie_section.description')}
          </p>
          {/* TODO: Check the size of this video */}
          <iframe
            title="Lara"
            src="https://player.vimeo.com/video/491162714?texttrack=en"
            allow="autoplay; fullscreen"
            className="aspect-video w-full border-0 outline-none"
          />
        </div>
      </section>
      <section className="bg-light-violet-100 px-4">
        <div className="mx-auto flex max-w-[60rem] flex-col items-center gap-16 py-16 text-center lg:gap-[6rem] lg:py-[6rem]">
          <p>{t('footer_section.language_importance')}</p>
          <p className="text-display-sm font-medium lg:text-display-md">
            {t('footer_section.understand_and_be_understood')}
          </p>
          <Anchor href="https://translated.com" target="_blank">
            <div className="scale-75 lg:scale-100">
              <TranslatedLogo />
            </div>
          </Anchor>
        </div>
      </section>
    </main>
  )
}
